import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { CmsNameValue, compareCmsNameValue } from "../../../../../api/cms/cms.types";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Observable, Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { CmsCachedService } from "../../../../../api/cms/cms-cached.service";
import { JobOffer } from "../../../../../api/job-offer/job-offer.types";
import { MissionPartnerAccount } from "../../../home-admin-data/account.types";

@Component({
  selector: "mh-job-offer-form",
  templateUrl: "./job-offer-form.component.html",
  styleUrls: ["./job-offer-form.component.less"],
})
export class JobOfferFormComponent implements OnInit, OnDestroy {
  @Input() jobOffer!: JobOffer;
  @Input() missionPartners!: Array<MissionPartnerAccount>;
  @Input() missionPartnerEditable = false;
  @Output() formChange = new EventEmitter<JobOffer>();

  formGroup!: UntypedFormGroup;
  private readonly destroy$ = new Subject<void>();

  public workAssignment$: Observable<Array<CmsNameValue>>;
  public expertFields$: Observable<Array<CmsNameValue>>;
  public sectors$: Observable<Array<CmsNameValue>>;
  public languageOptions$: Observable<Array<CmsNameValue>>;
  public industry$: Observable<Array<CmsNameValue>>;
  public careerLevel$: Observable<Array<CmsNameValue>>;
  public spokenLanguages$: Observable<Array<CmsNameValue>>;
  public degree$: Observable<Array<CmsNameValue>>;

  get experienceYears(): string {
    const exp = this.formGroup.get("workExperience")?.value || 0;
    return this.numToYearString(exp, 30);
  }

  get leadershipYears(): string {
    const exp = this.formGroup.get("leadershipExperience")?.value || 0;
    return this.numToYearString(exp, 30);
  }

  private numToYearString(value: number, max = 10) {
    let suffix = value == 1 ? "year.label" : "years.label";
    suffix = this.translateService.instant(suffix);
    return "<b>" + (value == max ? value + "+" : value) + "</b>" + " " + suffix;
  }

  get locationsControl(): AbstractControl | null {
    return this.formGroup.get("locations");
  }

  get countryControl(): AbstractControl | null {
    return this.formGroup.get("countryCode");
  }

  constructor(
    private fb: UntypedFormBuilder,
    private readonly translateService: TranslateService,
    private cmsCachedService: CmsCachedService,
  ) {
    this.workAssignment$ = this.cmsCachedService.getUserDataByName("ideal-working-assignment");
    this.expertFields$ = this.cmsCachedService.getUserDataByName("expert-field");
    this.sectors$ = this.cmsCachedService.getUserDataByName("sector");
    this.languageOptions$ = this.cmsCachedService.getUserDataByName("preferred-languages");
    this.industry$ = this.cmsCachedService.getUserDataByName("industry");
    this.careerLevel$ = this.cmsCachedService.getUserDataByName("career-level", false);
    this.spokenLanguages$ = this.cmsCachedService.getUserDataByName("language");
    this.degree$ = this.cmsCachedService.getUserDataByName("degree");
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      missionPartnerId: [this.jobOffer?.missionPartnerId, [Validators.required]],
      forMissionPartnerId: [this.jobOffer?.forMissionPartnerId, []],
      name: [this.jobOffer?.name, [Validators.required]],
      description: [this.jobOffer?.description, []],
      salaryExpectations: [this.jobOffer?.salaryExpectations || {}, []],
      locations: [this.jobOffer?.locations || [], [Validators.required]],
      workingModes: [this.jobOffer?.workingModes, []],
      jobSharing: [this.jobOffer?.jobSharing, []],
      domainExpertise: [this.jobOffer?.domainExpertise, []],
      workExperience: [this.jobOffer?.workExperience, []],
      leadershipExperience: [this.jobOffer?.leadershipExperience, []],
      leadershipTeamSize: [this.jobOffer?.leadershipTeamSize, []],
      sectorFocus: [this.jobOffer?.sectorFocus, []],
      workLanguage: [this.jobOffer?.workLanguage, []],
      industries: [this.jobOffer?.industries, []],
      department: [this.jobOffer?.department, []],
      careerLevel: [this.jobOffer?.careerLevel, []],
      degree: [this.jobOffer?.degree, []],
      countryCode: [this.jobOffer?.countryCode, []],
      spokenLanguages: [this.jobOffer?.spokenLanguages, []],
    });

    this.updateLocationsControl(this.jobOffer);

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.updateLocationsControl(value);
      this.formChange.next({ ...this.jobOffer, ...value });
    });
  }

  isValid(): boolean {
    this.formGroup.updateValueAndValidity();
    if (!this.formGroup.valid) {
      Object.values(this.formGroup.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });

      const salaryExpectationsControl = this.formGroup.get("salaryExpectations");
      if (salaryExpectationsControl) {
        salaryExpectationsControl.markAsDirty();
        salaryExpectationsControl.updateValueAndValidity({ onlySelf: true });
      }
    }
    return this.formGroup.valid;
  }

  protected updateLocationsControl(value: Partial<JobOffer>) {
    if (value?.countryCode) {
      this.countryControl?.setValidators([]);
      this.locationsControl?.setValidators([]);
      this.locationsControl?.disable({ onlySelf: true, emitEvent: false });
    } else if (this.locationsControl?.enabled && !this.locationsControl?.value.length) {
      this.countryControl?.setValidators([Validators.required]);
    } else {
      this.countryControl?.setValidators([]);
      this.locationsControl?.enable({ onlySelf: true, emitEvent: false });
      this.locationsControl?.setValidators([Validators.required]);
    }
    this.countryControl?.updateValueAndValidity({ onlySelf: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  protected readonly compareCmsNameValue = compareCmsNameValue;
}
